import ColumnConfigurationIcon from '@/assets/icons/ColumnConfigurationIcon'
import DownloadIcon from '@/assets/icons/DownloadIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import { ConfirmationDialog } from '@/components/common'
import ColumnConfigurationBase from '@/components/common/columnConfiguration'
import Search from '@/components/common/Search'
import CustomButton from '@/components/Returns/CustomButton'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import {
    deleteRule,
    downloadRules,
    fetchServiceMasterValues,
    searchRules,
    triggerRuleDownload,
    type RuleSearchFilter,
} from '@/multi-territory/mt-services/mt-rule-setting-api'
import type { MTMetadataTableColumn } from '@/multi-territory/mt-types/mt-metadata'
import { formatMTHeaderLabel } from '@/multi-territory/mt-utils/mt-search-fields'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { MTRuleSettingForm, type RuleSettingRow } from './MTRuleSettingForm'

interface RuleSettingTableRow extends RuleSettingRow {
  createdBy: string
  createdAt: string
  updatedBy: string
  updatedAt: string
}

const RuleSettingSearch = Search as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ColumnConfiguration = ColumnConfigurationBase as any

const TABLE_COLUMNS: MTMetadataTableColumn[] = [
  {
    id: 'ruleName',
    label: 'Rule name',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '100px',
  },
  {
    id: 'mappedNatureOfService',
    label: 'Mapped nature of service',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '180px',
  },
  {
    id: 'startDate',
    label: 'Start date',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '110px',
  },
  {
    id: 'endDate',
    label: 'End date',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '110px',
  },
  {
    id: 'status',
    label: 'Status',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '90px',
    render: (value: any) => {
      const isActive =
        value === true ||
        value === 'true' ||
        value === 'Yes' ||
        value === 'Active'
      return (
        <span
          style={{
            backgroundColor: isActive
              ? 'var(--bg-success)'
              : 'var(--table-header-bg)',
            color: 'var(--forbrand-primary)',
            padding: '4px 8px',
            borderRadius: 4,
            display: 'inline-block',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
  },
  {
    id: 'createdBy',
    label: 'Created by',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '140px',
  },
  {
    id: 'createdAt',
    label: 'Created at',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '170px',
  },
  {
    id: 'updatedBy',
    label: 'Updated by',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '140px',
  },
  {
    id: 'updatedAt',
    label: 'Updated at',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '170px',
  },
  {
    id: '_action',
    label: 'Action',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '90px',
    width: '90px',
  },
]

// Advance-search field labels are formatted with the SHARED MT header
// formatter so each field reads exactly like its table header column
// ("Rule name", "Mapped nature of service", "Created by"…) with acronyms
// (ID/TIN/…) kept uppercase. Placeholders reuse the formatted label so they
// stay in sync too. The `active` field intentionally keeps the "Status" label
// to match the Status column header (its id differs from the column id).
const BASE_SEARCH_FIELDS = [
  {
    id: 'ruleName',
    label: formatMTHeaderLabel('Rule name'),
    type: 'text',
    placeholder: `Enter ${formatMTHeaderLabel('Rule name')}`,
  },
  {
    id: 'mappedNatureOfService',
    label: formatMTHeaderLabel('Mapped nature of service'),
    type: 'dropdown',
    placeholder: 'Select nature of service',
    options: [] as { label: string; value: string }[],
  },
  {
    id: 'startDate',
    label: formatMTHeaderLabel('Start date'),
    type: 'date',
    placeholder: 'DD/MM/YYYY',
  },
  {
    id: 'endDate',
    label: formatMTHeaderLabel('End date'),
    type: 'date',
    placeholder: 'DD/MM/YYYY',
  },
  {
    id: 'active',
    label: formatMTHeaderLabel('Status'),
    type: 'dropdown',
    placeholder: 'Select status',
    options: [
      { label: 'Active', value: 'true' },
      { label: 'Inactive', value: 'false' },
    ],
  },
  {
    id: 'createdBy',
    label: formatMTHeaderLabel('Created by'),
    type: 'text',
    placeholder: `Enter ${formatMTHeaderLabel('Created by')}`,
  },
  {
    id: 'createdAt',
    label: formatMTHeaderLabel('Created at'),
    type: 'date',
    placeholder: 'DD/MM/YYYY',
  },
  {
    id: 'updatedBy',
    label: formatMTHeaderLabel('Updated by'),
    type: 'text',
    placeholder: `Enter ${formatMTHeaderLabel('Updated by')}`,
  },
  {
    id: 'updatedAt',
    label: formatMTHeaderLabel('Updated at'),
    type: 'date',
    placeholder: 'DD/MM/YYYY',
  },
]

/**
 * MTRuleSettingTable - Rule Setting list table.
 * Calls searchRules API when the View button is clicked.
 * Add/Edit opens a fullscreen Dialog (same pattern as MTDuplicationRuleTable).
 */
export function MTRuleSettingTable() {
  const [data, setData] = useState<RuleSettingTableRow[]>([])
  const [loading, setLoading] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const searchIconRef = useRef<HTMLButtonElement>(null)
  const [searchFilters, setSearchFilters] = useState<RuleSearchFilter[]>([])
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    { id: 1, logicalOperator: 'Where', field: 'ruleName', value: '' },
  ])

  // Dialog state (same pattern as duplication rule)
  const [formOpen, setFormOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<RuleSettingRow | null>(null)

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)

  // Column configuration state (show/hide columns, same pattern as Payee Master)
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] =
    useState<HTMLElement | null>(null)
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])

  // Search fields with dynamic dropdown options
  const [searchFields, setSearchFields] = useState<any[]>(BASE_SEARCH_FIELDS)

  const { showSnackbar } = useSnackbar()

  // Redux selectors
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country.countryCode
  )
  const selectedEntityUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedEntityUuid ?? ''
  )
  const selectedTanUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedTanUuid ?? ''
  )
  const entityMetaByUuid = useSelector(
    (state: any) => state.mtEntityTan?.entityMetaByUuid ?? {}
  )
  const viewTrigger = useSelector(
    (state: any) => state.mtEntityTan?.viewTrigger ?? 0
  )
  const lastViewTriggerRef = useRef(viewTrigger)

  // Derive tanApplicable from entity metadata
  const selectedEntityMeta = entityMetaByUuid[selectedEntityUuid] || null
  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true

  const apiIdentifierUuid = tanApplicable ? selectedTanUuid : selectedEntityUuid

  // --- Fetch service master values for search dropdown ---
  useEffect(() => {
    if (!countryCode) return
    fetchServiceMasterValues(countryCode).then(result => {
      if (result.success && Array.isArray(result.data)) {
        const options = result.data.map((val: string) => ({
          label: val,
          value: val,
        }))
        setSearchFields(prev =>
          prev.map(f =>
            f.id === 'mappedNatureOfService' ? { ...f, options } : f
          )
        )
      }
    })
  }, [countryCode])

  // --- API call: load rules ---
  const loadData = useCallback(
    async (filters?: RuleSearchFilter[]) => {
      if (!countryCode || !apiIdentifierUuid) return
      setLoading(true)

      const result = await searchRules(
        countryCode,
        apiIdentifierUuid,
        {
          searchFilters: filters ?? searchFilters,
          pageFilter: {
            currentPage,
            pageSize: itemsPerPage,
            sortField: 'lastModifiedDate',
            sortOrder: 'DESC',
          },
        },
        { tanApplicable }
      )

      if (result.success) {
        const responseData = result.data as any
        const content: any[] = responseData?.content ?? responseData?.data ?? []
        const total: number =
          responseData?.totalElements ?? responseData?.total ?? content.length

        setData(
          content.map((row: any, idx: number) => ({
            uuid: row.uuid || row.ruleId || `row-${idx}`,
            ruleName: row.ruleName ?? '',
            mappedNatureOfService:
              row.mappedNatureOfService ?? row.mappedServiceValue ?? '',
            startDate: row.startDate ?? '',
            endDate: row.endDate ?? '',
            status:
              row.active === true
                ? 'Active'
                : row.active === false
                  ? 'Inactive'
                  : (row.status ?? ''),
            createdBy: row.createdBy ?? '',
            createdAt:
              row.createdAt ?? row.createdDate ?? row.creationDate ?? '',
            updatedBy: row.updatedBy ?? row.lastModifiedBy ?? '',
            updatedAt: row.updatedAt ?? row.lastModifiedDate ?? '',
          }))
        )
        setTotalItems(total)
      } else {
        showSnackbar(result.error ?? 'Failed to load rules', 'error')
      }

      setLoading(false)
    },
    [
      countryCode,
      apiIdentifierUuid,
      tanApplicable,
      currentPage,
      itemsPerPage,
      searchFilters,
      showSnackbar,
    ]
  )

  // Load data when View button is clicked (viewTrigger increments)
  useEffect(() => {
    if (viewTrigger > 0 && apiIdentifierUuid && countryCode) {
      if (viewTrigger <= lastViewTriggerRef.current) return
      lastViewTriggerRef.current = viewTrigger
      setShowTable(true)
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTrigger])

  // Reload data when pagination changes (only if table is already shown)
  useEffect(() => {
    if (showTable && apiIdentifierUuid && countryCode) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage])

  // --- Add/Edit handlers (dialog-based, same as duplication rule) ---
  const handleAdd = () => {
    setEditingRule(null)
    setFormOpen(true)
  }

  const handleEdit = (uuid: string) => {
    const row = data.find(r => r.uuid === uuid)
    if (row) {
      setEditingRule(row)
      setFormOpen(true)
    }
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setEditingRule(null)
  }

  const handleFormSuccess = () => {
    setFormOpen(false)
    setEditingRule(null)
    loadData()
  }

  const handleDelete = (uuid: string) => {
    setDeletingUuid(uuid)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingUuid || !countryCode || !apiIdentifierUuid) return
    setDeleteDialogOpen(false)
    setLoading(true)
    const result = await deleteRule(
      countryCode,
      deletingUuid,
      apiIdentifierUuid,
      {
        tanApplicable,
      }
    )
    if (result.success) {
      showSnackbar('Rule deleted successfully', 'success')
      await loadData()
    } else {
      showSnackbar(result.error ?? 'Failed to delete rule', 'error')
    }
    setDeletingUuid(null)
    setLoading(false)
  }

  const handlePageChange = (page: number) => setCurrentPage(page)
  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size)
    setCurrentPage(1)
  }

  const handleDownload = async () => {
    if (!countryCode || !apiIdentifierUuid) {
      showSnackbar('Please select an entity first', 'error')
      return
    }
    showSnackbar('Download started', 'info')
    const result = await downloadRules(
      countryCode,
      apiIdentifierUuid,
      searchFilters,
      { tanApplicable }
    )
    if (result.success) {
      triggerRuleDownload(result.data, `rule-settings.xlsx`)
      showSnackbar('Download complete', 'success')
    } else {
      showSnackbar(
        !result.success ? result.error : 'Failed to download rules',
        'error'
      )
    }
  }

  const openSearchDialog = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setSearchOpen(true)
  }
  const closeSearchDialog = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  const applySearch = (searchList: any[]) => {
    // Search component already returns formatted filters: { fieldName, operator, value, filterOperator }
    const filters: RuleSearchFilter[] = searchList.map((s: any) => ({
      fieldName: s.fieldName,
      operator: s.operator,
      value: s.value,
      ...(s.filterOperator && { filterOperator: s.filterOperator }),
      ...(s.searchValueList && { searchValueList: s.searchValueList }),
    }))
    setSearchFilters(filters)
    setCurrentPage(1)
    setSearchOpen(false)
    loadData(filters)
  }

  const resetSearch = () => {
    setSearchCriteria([
      { id: 1, logicalOperator: 'Where', field: 'ruleName', value: '' },
    ])
    setSearchFilters([])
    setSearchOpen(false)
    setCurrentPage(1)
    loadData([])
  }

  // --- Column configuration ---
  // The `_action` column is always shown and cannot be toggled off.
  const configurableColumns = useMemo(
    () => TABLE_COLUMNS.filter(col => col.id !== '_action'),
    []
  )

  const visibleColumns = useMemo(
    () => TABLE_COLUMNS.filter(col => !hiddenColumns.includes(col.id)),
    [hiddenColumns]
  )

  const columnConfigColumns = useMemo(
    () => configurableColumns.map(col => ({ id: col.id, label: col.label })),
    [configurableColumns]
  )

  const columnConfigVisibleIds = useMemo(
    () =>
      configurableColumns
        .filter(col => !hiddenColumns.includes(col.id))
        .map(col => col.id),
    [configurableColumns, hiddenColumns]
  )

  const handleColumnConfigOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnConfigAnchor(event.currentTarget)
    setColumnConfigOpen(prev => !prev)
  }

  const handleColumnConfigClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleColumnChange = (selectedColumns: string[]) => {
    const newHidden = configurableColumns
      .filter(col => !selectedColumns.includes(col.id))
      .map(col => col.id)
    setHiddenColumns(newHidden)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {showTable && (
        <>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              my: '16px',
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                lineHeight: '130%',
                color: 'var(--text-heading)',
              }}
            >
              Rule setting
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Column Configuration">
                <IconButton
                  size="medium"
                  onClick={handleColumnConfigOpen}
                  aria-label="Column Configuration"
                >
                  <ColumnConfigurationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download Excel">
                <IconButton
                  size="medium"
                  onClick={handleDownload}
                  aria-label="Download"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Search">
                <IconButton
                  size="medium"
                  ref={searchIconRef}
                  onClick={openSearchDialog}
                  aria-label="Search"
                >
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--border-subtel)',
                }}
              />
              <CustomButton onClick={handleAdd}>Add</CustomButton>
            </Box>
          </Box>

          {/* Table */}
          <MTCommonTable
            columns={visibleColumns}
            data={data}
            loading={loading}
            showCheckbox={false}
            showFooter
            onRowEdit={handleEdit}
            onRowDelete={handleDelete}
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            emptyMessage="No Rule Found"
          />

          {/* Search */}
          <RuleSettingSearch
            open={searchOpen}
            anchorEl={anchorEl}
            fields={searchFields as any}
            searchCriteria={searchCriteria}
            setSearchCriteria={setSearchCriteria}
            onClose={closeSearchDialog}
            onApply={applySearch}
            onReset={resetSearch}
            placement="bottom-start"
          />

          {/* Column Configuration */}
          <ColumnConfiguration
            columns={columnConfigColumns}
            visibleColumns={columnConfigVisibleIds}
            anchorEl={columnConfigAnchor}
            open={columnConfigOpen}
            onColumnChange={handleColumnChange}
            onClose={handleColumnConfigClose}
          />
        </>
      )}

      {/* Add/Edit Form Dialog */}
      <MTRuleSettingForm
        open={formOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        editingRule={editingRule}
        countryCode={countryCode}
        apiIdentifierUuid={apiIdentifierUuid}
        tanApplicable={tanApplicable}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setDeletingUuid(null)
        }}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this rule?"
        message="Deleting this Rule will impact the service suggested by the tool. Do you want to continue?"
        confirmText="Proceed"
        type="warning"
        showCancelButton={true}
        onCancel={() => {
          setDeleteDialogOpen(false)
          setDeletingUuid(null)
        }}
      />
    </Box>
  )
}
